/**
 * Agenda.js
 * This component is loaded on selection of a meeting and is the parent of multiple AgendaItem.js
 *  - Whether to show/hide the columns is calculated here and stored in the MeetingStore
 *  - The numbering information is calculated here and sent as a prop to AgendaItem.js
 *  - The animations opening/closing this page are driven here by listening to the many animation end events
 */
import React from 'react';
import { observer } from 'mobx-react';
import { action } from 'mobx';
import { MTG_VIEW_SINGLE } from '../../../../helpers/constants';

import MeetingCard from './MeetingCard';
import AgendaItem from './AgendaItem';
import Governance from '../../Dashboard/components/Governance/Governance';
import Attendee from '../../Meeting/components/Attendee';

import store from '../../../../stores/store';
import { TabContent, TabPane, Nav, NavItem, NavLink, Row, Col } from 'reactstrap';
import classNames from 'classnames';
import { DOC_TYPE_AGENDA, GOV_DEFAULT_TITLE } from '../../../../helpers/constants';
import * as local from '../../../../helpers/localStorage';
import {
    // ERROR_OPEN_DOCUMENT_MSG,
    // ERROR_OPEN_DOCUMENT_TITLE,
    // DOC_TOKEN_ERROR_HEADER_MSG,
    // DOC_TOKEN_ERROR_CONTENT_MSG,
    DOC_NOTDOWNLOADED_MSG,
    // DOC_NOTDOWNLOADED_TITLE,
} from '../../../../helpers/messageText';
import _ from 'lodash';

const Agenda = observer(
    class Agenda extends React.Component {
        constructor(props) {
            super(props);

            var displayButtonStatus = 'none';
            if (window.offlineApiExtension && store.conn.isOnline) {
                displayButtonStatus = '';
            }

            this.toggle = this.toggle.bind(this);
            this.state = {
                activeTab: '1',
                displayButton: displayButtonStatus,
                displayPercentage: 'none',
                percentageBar: '0',
                displayUnreadUsers: 1,
                numberingType: 2,     // 1 - alphanumeric; 2 - numeric; 3 - manual
                
                set: action((field, value) => {
                    this[field] = value;
                })
            };
        }

        toggle(tab) {
            if (this.state.activeTab !== tab) {
                this.setState({
                    activeTab: tab,
                });
            }
        }

        componentDidMount() {
            this.addListenerMulti();
            this.checkTableColumns();
            this.toggleIfNoContent();
        }

        componentWillUpdate(nextProps, nextState) {
            this.checkTableColumns();
        }

        componentWillUnmount() {
            this.removeListenerMulti();
        }

        /**
         * It returns group's governance title
         * @return {string}  Govenance Title name
         */
        getTitleOfGovernance = () => {
            const meeting =
                store.meetings.selectedMeeting !== null ? store.meetings.selectedMeeting[0] : null;
            if (store.groups.selectedId === null && meeting !== null) {
                const instanceId = this.props.location.search.substring(1);
                if (instanceId !== undefined && instanceId.length > 0) {
                    const groupId = meeting.GroupId;
                    const governanceTitle = store.companies.getGroupGovernanceTitle(
                        instanceId,
                        groupId,
                    );
                    // return 'View ' + governanceTitle;
                    return governanceTitle;
                }
            }
            return GOV_DEFAULT_TITLE;
        };

        /******************************************************************************
        captureAnimationEnd
        - IHT, Oct 17
        - need to listen for the end of the animations that open/close the Agenda page
        - open: only now can we resize the map correctly
        - close: only now can we redirect
        ******************************************************************************/
        captureAnimationEnd = () => {
            const domAgenda = document.getElementById('agenda');
            store.companies.routeBeforeRefresh = '';

            if (domAgenda.classList.value.indexOf('zoomOut') !== -1) {
                this.props.history.push('/dashboard/meetings');
            }
        };

        addListenerMulti() {
            const el = document.getElementById('agenda');
            // const s = "webkitTransitionEnd otransitionend oTransitionEnd msTransitionEnd transitionend";
            const s =
                'webkitAnimationEnd mozAnimationEnd MSAnimationEnd oanimationend animationend';
            const fn = this.captureAnimationEnd;

            s.split(' ').forEach(e => {
                // console.log("addListenerMulti", e, el, fn);
                el.addEventListener(e, fn, false);
            });
        }

        removeListenerMulti() {
            const el = document.getElementById('agenda');
            const s =
                'webkitAnimationEnd mozAnimationEnd MSAnimationEnd oanimationend animationend';
            const fn = this.captureAnimationEnd;

            s.split(' ').forEach(e => {
                // console.log("removeListenerMulti", e, el, fn);
                el.removeEventListener(e, fn, false);
            });
        }

        /**
         * It closes modal window with group's agenda/governance
         * if groups's unselectedGroup is true, set to false
         */
        agendaAnimations = () => {
            // IHT 9 Nov 17 - removing closing animation as it looks rubbish - will be replaced later
            // const domAgenda = document.getElementById('agenda');
            // domAgenda.classList.remove('zoomInUp');
            // domAgenda.classList.add('zoomOutDown');
            store.companies.routeBeforeRefresh = '';
            store.groups.resetVisualIndicatorsToSelectedGroup();
            this.props.history.push('/dashboard/meetings');
        };

        checkTableColumns = () => {
            const meeting =
                store.meetings.selectedMeeting !== null ? store.meetings.selectedMeeting[0] : null;
            let showNumbering = 0;
            let showCategory = 0;
            let showPresenter = 0;
            let showDuration = 0;
            let showDocuments = 0;

            if (meeting === undefined || meeting === null) {
                store.meetings.displayAgendaTable(false);
            } 
            else if(meeting.Agenda.length === 0) {
                store.meetings.displayAgendaTable(false);

                if (this.state.activeTab === '1') {
                    this.toggleIfNoContent();
                }
            }
            else {
                store.meetings.displayAgendaTable(true);
                meeting.Agenda.forEach((details, i) => {
                    if (
                        details.ItemNumberString !== null &&
                        details.ItemNumberString.trim() !== ''
                    ) {
                        showNumbering++;
                    }
                    if (details.Category !== null) {
                        showCategory++;
                    }
                    if (details.Presenter !== null) {
                        showPresenter++;
                    }
                    if (details.Duration !== '0') {
                        showDuration++;
                    }
                    if (details.AgendaItemDocs.length !== 0) {
                        showDocuments++;
                    }
                });

                const settings = {
                    showTable: true,
                    showColumns: {
                        numbering: showNumbering > 0,
                        category: showCategory > 0,
                        presenter: showPresenter > 0,
                        duration: showDuration > 0,
                        documents: showDocuments > 0,
                    },
                };

                const currentSettings = { ...store.meetings.agendaTable.showColumns };

                //only reset the table if there is a difference
                if (!_.isEqual(currentSettings, settings.showColumns)) {
                    store.meetings.setAgendaTable(settings);
                }
            }

            // console.log("checkTableColumns", {showNumbering, showCategory, showPresenter, showDuration, showDocuments});
        };

        numberingNomenclature = meeting => {
            const ALPHAROMAN = 1; //Display alpha roman numbering
            const MANUAL = 3; //Display manual numbering
            let nomenclature = {};         

            if (meeting !== undefined && meeting !== null) {
                let numberingType = meeting.Numbering;
                let numberingLevels = {};

                meeting.Agenda.forEach((detail, i) => {
                    const numbText = detail.ItemNumberString;
                    const arrNumbering = detail.IndrasNumbering.split('.');
                    const lastIndex = arrNumbering.length - 1;
                    let subNumbering = numberingLevels[lastIndex] || {};

                    subNumbering[arrNumbering[lastIndex]] = numbText;
                    numberingLevels[lastIndex] = subNumbering;
                });

                switch (numberingType) {
                    case ALPHAROMAN:
                        numberingLevels['delimiter'] = '';
                        this.state.set('numberingType', 1);
                        break;

                    case MANUAL:
                        numberingLevels['delimiter'] = ' ';
                        this.state.set('numberingType', 3);
                        break;

                    default:
                        numberingLevels['delimiter'] = '.';
                        this.state.set('numberingType', 2);
                }

                nomenclature = numberingLevels;
            }

            return nomenclature;
        };

        toggleIfNoContent() {
            const shouldShowAgenda = store.meetings.agendaTable.showTable;
            const shouldShowGovernance = this.displayGovTab();

            if (!shouldShowAgenda && !shouldShowGovernance) {
                this.toggle('3');
            } else if (!shouldShowAgenda) {
                this.toggle('2');
            }
        }

        displayGovTab = () => {
            if (store.groups.loadingGov) {
                if (store.groups.isGovernanceExists) {
                    if (store.groups.isGovernanceExists.length > 0) {
                        return true;
                    }
                }
            }
            return false;
        };

        onClickShowUnreadUsers = async () => {
            if (this.state.displayUnreadUsers === 1) {
                this.setState({
                    displayUnreadUsers: 0,
                });
            } else {
                this.setState({
                    displayUnreadUsers: 1,
                });
            }
        };

        onClickDownloadAll = async () => {
            // console.log('this.state.displayButton', this.state.displayButton);

            this.setState({
                displayButton: 'none',
                displayPercentage: '',
                percentageBar: '0',
            });
            // console.log('this.state.displayButton', this.state.displayButton);

            // OK we first have to get a count of ALL docuemnts associated to the meeting
            const meeting =
                store.meetings.selectedMeeting !== null ? store.meetings.selectedMeeting[0] : null;

            var totalDocumentCount = 0;
            var documentDownloadedCount = 0;

            if (meeting === null || meeting.Agenda.length === 0) {
                store.meetings.displayAgendaTable(false);
            } else {
                const instanceId = meeting.InstanceId;

                const keyDownloaded =
                    local.LS_AGENDAS_DOWNLOADED +
                    instanceId +
                    meeting.MeetingId +
                    store.user.profile[0].UserId;

                meeting.Agenda.forEach(async (details, i) => {
                    // For each agenda we have to get the agenda document count
                    totalDocumentCount = totalDocumentCount + details.AgendaItemDocs.length;

                    // OK we now loop through each agenda item to get the document ID
                    let agendaDocs = Array.from(details.AgendaItemDocs);

                    for (var agendaDocItem of agendaDocs) {
                        // if (local.isKeyExists(keyDownloaded, agendaDocItem.AgendaDocId)) {
                        //     console.log('EXISTS' + agendaDocItem.AgendaDocId);
                        // } else {
                        //     console.log('NOT EXISTS' + agendaDocItem.AgendaDocId);
                        // }

                        // We await for the document to download before continue to bexr
                        let result = await store.documents.downloadDocumentOnly({
                            instanceId,
                            docTypeId: DOC_TYPE_AGENDA,
                            docId: agendaDocItem.AgendaDocId,
                            DocExtension: agendaDocItem.DocExtension,
                            DocPermissions: agendaDocItem.DocPermissions,
                            DocName: agendaDocItem.DocName,
                            DocAnnotations: agendaDocItem.DocAnnotations,
                            onClose: this.props.onToggle,
                        });

                        // This should happen only when document has been downloaded and, therefore, idb fails to retrieve document

                        if (result == null) {
                            // console.log('not downloaded.');
                            throw new Error(DOC_NOTDOWNLOADED_MSG);
                        } else {
                            documentDownloadedCount++;

                            // We save the ID of the downloaded document
                            // Why is life so hard... could have just put in a variable but Noooooo... have to create a new
                            // local storage so we don't change the existing API... Sobb...
                            local.saveNumberByKeyStringInLocalStorageArray(
                                keyDownloaded,
                                agendaDocItem.AgendaDocId,
                            );

                            this.setState({
                                percentageBar:
                                    Math.round(documentDownloadedCount / totalDocumentCount * 100) +
                                    '%',
                            });

                            // console.log('math', Math.round(documentDownloadedCount / totalDocumentCount * 100));
                        }
                    }

                    if (documentDownloadedCount === totalDocumentCount) {
                        // When finished display the button again
                        this.setState({
                            displayButton: '',
                            displayPercentage: 'none',
                        });
                    }
                });

                return true;
            }
        };

        buildDownloadButton = isOnline => {
            if (this.state.activeTab.toString() === '1' && window.offlineApiExtension) {
                if (isOnline) {
                    return (
                        <button
                            type="button"
                            className="btn btn-outline-secondary ml-2"
                            id="downloadAllAgenda"
                            onClick={() => {
                                this.onClickDownloadAll();
                            }}
                        >
                            <i className="fa fa-download mr-2" aria-hidden="true" />Download
                            documents
                        </button>
                    );
                } else {
                    return (
                        <button className="btn btn-secondary disabled ml-auto" type="button">
                            <i className="fa fa-exclamation" aria-hidden="true" />
                            <span className="pl-3">OFFLINE</span>
                        </button>
                    );
                }
            }
        };

        buildViewUnreadButton = isOnline => {
            const meeting =
                store.meetings.selectedMeeting !== null ? store.meetings.selectedMeeting[0] : null;

            var displayButtonStatus =
                this.state.displayUnreadUsers === 1
                    ? 'fa fa-toggle-on mr-2'
                    : 'fa fa-toggle-off mr-2';

            if (isOnline && this.state.activeTab.toString() === '1') {
                if (meeting.TrackReadDocument === 1) {
                    if (store.groups.selectedGroup != null) {
                        if (
                            store.groups.selectedGroup.IsGroupAdmin === 1 ||
                            store.groups.selectedGroup.IsLead === 1
                        ) {
                            return (
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary no-border-btn"
                                    id="showUnreadUsers"
                                    onClick={() => {
                                        this.onClickShowUnreadUsers();
                                    }}
                                >
                                    <i className={displayButtonStatus} aria-hidden="true" />Unopened
                                    users
                                </button>
                            );
                        }
                    }
                }
            }
        };

        render() {
            const meeting =
                store.meetings.selectedMeeting !== null ? store.meetings.selectedMeeting[0] : null;

                const shouldShowAgenda = store.meetings.agendaTable.showTable;
                const showColumns = store.meetings.agendaTable.showColumns;
                const shouldShowGovernance = this.displayGovTab();
                const nomenclature = meeting !== undefined ? this.numberingNomenclature(meeting) : null;
                
                if(meeting !== undefined && meeting !== null) { 
                    return (
                        <div className="card d-flex flex-column mb-3 animated zoomIn" id="agenda">
                            <MeetingCard
                                ref="AgendaDetails"
                                viewType={MTG_VIEW_SINGLE}
                                i={1}
                                id={meeting.MeetingActionText}
                                details={meeting}
                                instanceId={meeting.InstanceId}
                                coIndex={store.companies.indexFromId(meeting.InstanceId)}
                                agendaAnimations={this.agendaAnimations}
                            />

                            {/* <!-- Agenda --> */}
                            <div className="" id="agenda-details">
                                <div className="container-fluid p-0">
                                    <div className="sticky-box p-2 my-auto">
                                        <Nav tabs className="nav-fill">
                                            {!shouldShowAgenda ? null : (
                                                <NavItem>
                                                    <NavLink
                                                        className={classNames({
                                                            active: this.state.activeTab === '1',
                                                        })}
                                                        onClick={() => {
                                                            this.toggle('1');
                                                        }}
                                                    >
                                                        <div className="agenda-tab">Agenda</div>
                                                    </NavLink>
                                                </NavItem>
                                            )}
                                            {shouldShowGovernance && (
                                                <NavItem>
                                                    <NavLink
                                                        className={classNames({
                                                            active: this.state.activeTab === '2',
                                                        })}
                                                        onClick={() => {
                                                            this.toggle('2');
                                                        }}
                                                    >
                                                        <div className="agenda-tab">
                                                            {store.groups.selectedId
                                                                ? //'View ' +
                                                                store.groups.selectedGroup.GovernanceTitle
                                                                : this.getTitleOfGovernance()}
                                                        </div>
                                                    </NavLink>
                                                </NavItem>
                                            )}
                                            <NavItem>
                                                <NavLink
                                                    className={classNames({
                                                        active: this.state.activeTab === '3',
                                                    })}
                                                    onClick={() => {
                                                        this.toggle('3');
                                                    }}
                                                >
                                                    <div className="agenda-tab">Attendees</div>
                                                </NavLink>
                                            </NavItem>
                                        </Nav>
                                    </div>
                                    <div className="d-flex flex-wrap justify-content-end mr-2">
                                        {this.buildViewUnreadButton(store.conn.isOnline)}
                                        {this.buildDownloadButton(store.conn.isOnline)}

                                        <div
                                            className="progress ml-2"
                                            style={{ display: this.state.displayPercentage }}
                                        >
                                            <div
                                                className="progress-bar"
                                                role="progressbar"
                                                aria-valuenow="25"
                                                aria-valuemin="0"
                                                aria-valuemax="100"
                                                style={{ width: this.state.percentageBar }}
                                            >
                                                {this.state.percentageBar}
                                            </div>
                                        </div>
                                    </div>
                                    <TabContent activeTab={this.state.activeTab} className="mt-2">
                                        <TabPane tabId="1" className="animated fadeIn">
                                            <Row>
                                                <Col sm="12">
                                                    <div className="table-responsive">
                                                        <table className="table table-bordered">
                                                            <thead className="thead-default">
                                                                <tr>
                                                                    {showColumns.numbering ? (
                                                                        <th className="item-number-heading">
                                                                            Item number
                                                                        </th>
                                                                    ) : null}

                                                                    <th>
                                                                        Title<br />
                                                                        <small>Description</small>
                                                                    </th>

                                                                    {showColumns.category ? (
                                                                        <th>Category</th>
                                                                    ) : null}
                                                                    {showColumns.presenter ? (
                                                                        <th>Presenter</th>
                                                                    ) : null}
                                                                    {showColumns.duration ? (
                                                                        <th>Time</th>
                                                                    ) : null}
                                                                    {showColumns.documents ? (
                                                                        <th className="flex-column">
                                                                            <span className="documents-lable-hide-sm">
                                                                                Documents
                                                                            </span>
                                                                            {/*<button
                                                                                type="button"
                                                                                className="btn btn-outline-secondary btn-sm download-all-btn mr-auto"
                                                                            >
                                                                                <i className="fa fa-download pr-2" aria-hidden="true" />Download
                                                                                all documents
                                                                    </button> */}
                                                                        </th>
                                                                    ) : null}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {meeting.Agenda.length > 0
                                                                    ? meeting.Agenda.map((detail, i) => (
                                                                        <AgendaItem
                                                                            key={i}
                                                                            i={detail.AgendaId}
                                                                            details={detail}
                                                                            instanceId={
                                                                                meeting.InstanceId
                                                                            }
                                                                            meeting={meeting}
                                                                            groupId={meeting.GroupId}
                                                                            nomenclature={nomenclature}
                                                                            displayList={
                                                                                this.state
                                                                                    .displayUnreadUsers
                                                                            }
                                                                            onToggle={this.toggle}
                                                                            numberingType={this.numberingType}
                                                                        />
                                                                    ))
                                                                    : null}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </TabPane>
                                        {shouldShowGovernance && (
                                            <TabPane tabId="2" className="animated fadeIn">
                                                <Row>
                                                    <Col sm="12">
                                                        <Governance />
                                                    </Col>
                                                </Row>
                                            </TabPane>
                                        )}
                                        <TabPane tabId="3" className="animated fadeIn">
                                            <Row>
                                                <Col sm="12">
                                                    <Attendee meeting={meeting} />
                                                </Col>
                                            </Row>
                                        </TabPane>
                                    </TabContent>
                                </div>
                            </div>
                        </div>);
                    }
                    else {
                        return (
                            <div className="card d-flex flex-column mb-3 animated zoomIn" id="agenda">
                                <div className="pr-2 pb-2 pl-2">
                                    <div className="row mt-2">
                                        <a className="closebtn-meeting" onClick={() => this.agendaAnimations()}>
                                            <i className="fa fa-times-thin fa-lg" aria-hidden="true" />
                                        </a>
                                        <div className="col-11 col-lg-6 pr-0">
                                            Meeting doesn't exist!
                                        </div>
                                    </div>
                                </div>                                
                            </div>
                        );
                    }
        }
    },
);

export default Agenda;



// WEBPACK FOOTER //
// ./src/containers/PrivateArea/Meeting/components/Agenda.js